import type { SectionInstance } from '@/lib/sections/types';
import { collectionUrl } from '@/lib/collectionUrl';
import { canonicalPath } from '@workspace/url-source';
import { FOOTER_CONFIG } from './footerConfig';
import { HERO_CONFIG } from './heroConfig';
import { CATEGORY_NAV_CONFIG } from './categoryNavConfig';
import { BOUQUET_CREATOR_ENABLED } from '@/lib/storeFeatures';

import gratulacjeImg    from '@assets/optimized_home/category-urodziny-480.webp';
import podziekowaniaImg from '@assets/optimized_home/category-dodatki-480.webp';
import niespodziankaImg from '@assets/optimized_home/category-ukochana-480.webp';
import przeprosinyImg   from '@assets/optimized_home/category-roze-480.webp';
import rozaCreatorImg       from '@assets/optimized_home/category-roze-480.webp';
import tulipanCreatorImg    from '@assets/optimized_home/product-pastelowa-480.webp';
import slonecznikCreatorImg from '@assets/optimized_home/category-bukiety-480.webp';
import mieszanyCreatorImg   from '@assets/optimized_home/kreator-background-480.webp';

/**
 * Stały układ i wartości bazowe strony głównej, kompilowane do bundla.
 * CMS nadpisuje tylko oznaczone w komponentach pola tekstów i istniejących
 * zdjęć. HOME_SECTIONS, kolejność, adresy, wybór produktów i style pozostają
 * w kodzie; panel nie przyjmuje konfiguracji całych sekcji.
 *
 * wrapperClassName steruje widocznością sekcji — część sekcji
 * (TodayDeliveryBanner, OrderSteps, GoogleReviews)
 * nie ma wbudowanej logiki mobile/desktop i ma osobny mobile-only odpowiednik
 * (Mobile*), więc musi zostać ukryta na mobile przez wrapperClassName.
 *
 * placeholderHeight — zmierzone bezpośrednio (Playwright, 1280×800 desktop /
 * 390×844 mobile, pełny wyrenderowany DOM, siatki produktowe w stanie
 * skeleton — ten sam rozmiar co po realnym załadowaniu, bo liczba
 * kolumn/kafelków jest stała niezależnie od danych). Gdy sekcja ma
 * wrapperClassName='hidden md:block' (ukryta na mobile), wartość `mobile`
 * jest nieistotna (element ma i tak display:none) — zostawiona jako 0.
 * Analogicznie dla mobile-only sekcji, wartość `desktop` jest nieistotna.
 *
 * google-reviews i mobile-reviews są WYJĄTKIEM: obie nie mają widocznej
 * zawartości, dopóki Google Reviews API nie odpowie (zostaje tylko schema CMS),
 * więc ich rzeczywistej wysokości nie da się zmierzyć bez żywego API —
 * wartości niżej to analityczne oszacowanie z rozmiaru pojedynczej karty
 * (`height: clamp(288px, 22vw, 320px)` w reviewStyles.ts) + nagłówek,
 * nie bezpośredni pomiar. Do doprecyzowania, gdy będzie dostęp do realnego API.
 *
 * config — treść każdej sekcji, JSON-serializable (patrz lib/sections/types.ts).
 * Referencje do komponentów ikon zostają WEWNĄTRZ komponentów (mapy
 * string→ikona) — tu przechodzą tylko klucze (np. `icon: 'roze'`).
 */
export const HOME_SECTIONS: SectionInstance[] = [
  {
    id: 'hero', type: 'hero', enabled: true, order: 0,
    config: HERO_CONFIG,
    // Hero jest renderowany eager przez SectionRenderer; placeholder nie jest używany.
    placeholderHeight: { mobile: 0, desktop: 0 },
  },
  {
    id: 'today-delivery', type: 'todayDeliveryBanner', enabled: true, order: 10,
    wrapperClassName: 'hidden md:block',
    config: {
      countdownLabel: 'Dostawa dziś? Zamów w ciągu:',
      nextDeliveryPrefix: 'Najbliższa możliwa dostawa:',
      nextDeliveryFromWord: 'od godziny',
      namedaysTodayLabel: 'Imieniny dziś obchodzą:',
      namedaysTomorrowLabel: 'Imieniny jutro obchodzą:',
      hourUnit: 'GODZ.',
      minuteUnit: 'MIN.',
      secondUnit: 'SEK.',
    },
    placeholderHeight: { mobile: 0, desktop: 54 },
  },
  {
    id: 'category-nav', type: 'categoryNav', enabled: true, order: 30,
    config: CATEGORY_NAV_CONFIG,
    placeholderHeight: { mobile: 183, desktop: 310 },
  },
  {
    id: 'bestsellers', type: 'popularBouquets', enabled: true, order: 40,
    config: { collectionHandle: 'bestsellery', limit: 12, title: 'Nasze bestsellery' },
    wrapperClassName: '-mt-[5px] md:mt-0',
    placeholderHeight: { mobile: 1191, desktop: 1455 },
  },
  {
    id: 'mobile-occasions', type: 'mobileOccasions', enabled: true, order: 50,
    config: {
      title: 'Kwiaty na każdą okazję',
      ctaLabel: 'Zobacz wszystkie →',
      ctaHref: collectionUrl('okazje'),
      occasions: [
        { title: 'Gratulacje',    href: collectionUrl('gratulacje'),    image: gratulacjeImg },
        { title: 'Podziękowania', href: collectionUrl('podziekowania'), image: podziekowaniaImg },
        { title: 'Niespodzianka', href: collectionUrl('niespodzianka'), image: niespodziankaImg },
        { title: 'Przeprosiny',   href: collectionUrl('przeprosiny'),   image: przeprosinyImg },
      ],
    },
    placeholderHeight: { mobile: 176, desktop: 0 },
  },
  {
    id: 'mobile-reviews', type: 'mobileReviews', enabled: true, order: 60,
    config: { title: 'Co mówią nasi klienci' },
    // Oszacowanie — patrz komentarz na górze pliku (API opinii niedostępne do pomiaru).
    placeholderHeight: { mobile: 220, desktop: 0 },
  },
  {
    id: 'order-steps', type: 'orderSteps', enabled: true, order: 70,
    wrapperClassName: 'hidden md:block',
    config: {
      eyebrow: 'Jak zamawiać',
      title: 'Twój wyjątkowy bukiet',
      titleEmphasis: 'w 4 prostych krokach',
      steps: [
        { number: '01', emoji: '🌸', title: 'Wybierz kompozycję', description: 'Wybierz bukiet, który najlepiej pasuje do wyjątkowej chwili.' },
        { number: '02', emoji: '🚚', title: 'Szczegóły dostawy',  description: 'Wybierz dostępny dzień i przedział dostawy pod wskazany adres.' },
        { number: '03', emoji: '💌', title: 'Bilecik i dodatki',  description: 'Napisz wiadomość do bileciku i wybierz dostępne dodatki.' },
        { number: '04', emoji: '💳', title: 'Płatność',           description: 'Wybierz jedną z metod płatności dostępnych przy zamówieniu.' },
      ],
    },
    placeholderHeight: { mobile: 0, desktop: 506 },
  },
  {
    id: 'featured', type: 'featuredProducts', enabled: true, order: 80,
    config: { tag: 'polecane', limit: 12, title: 'Polecane przez kwiaciarnię' },
    placeholderHeight: { mobile: 1196, desktop: 1296 },
  },
  {
    id: 'bouquet-creator', type: 'bouquetCreator', enabled: BOUQUET_CREATOR_ENABLED, order: 90,
    wrapperClassName: 'hidden md:block',
    config: {
      eyebrow: 'Kreatory',
      title: 'Stwórz swój bukiet',
      ctaLabel: 'Zobacz wszystkie',
      ctaHref: canonicalPath('/kreatory-bukietow'),
      description: 'Wybierz swoje ulubione kwiaty, określ ilość i stwórz kompozycję przygotowaną specjalnie dla Ciebie.',
      creators: [
        { id: 'roze',        name: 'Kreator Róż',           description: 'Stwórz wyjątkowy bukiet róż dopasowany do okazji.',              route: canonicalPath('/kreator/roze'),        shopifyHandle: 'kreator-roz',      staticImage: rozaCreatorImg,       iconKey: 'roze' },
        { id: 'tulipany',    name: 'Kreator Tulipanów',     description: 'Wybierz kolor i liczbę tulipanów do swojego bukietu.',             route: canonicalPath('/kreator/tulipany'),    shopifyHandle: 'tulipany',         staticImage: tulipanCreatorImg,    iconKey: 'tulipany' },
        { id: 'sloneczniki', name: 'Kreator Słoneczników',  description: 'Radosne słoneczniki - energia i ciepło w każdym bukiecie.',       route: canonicalPath('/kreator/sloneczniki'), shopifyHandle: 'sloneczniki',      staticImage: slonecznikCreatorImg, iconKey: 'sloneczniki' },
        { id: 'gerbery',     name: 'Kreator Gerber',        description: 'Kolorowe gerbery - wybierz barwy i liczbę kwiatów.',             route: canonicalPath('/kreator/gerbery'),     shopifyHandle: 'gerbery',           staticImage: mieszanyCreatorImg,   iconKey: 'gerbery' },
        { id: 'lilie',       name: 'Kreator Lilii',         description: 'Eleganckie lilie w bukiecie skomponowanym po Twojemu.',           route: canonicalPath('/kreator/lilie'),       shopifyHandle: 'lilie',             staticImage: rozaCreatorImg,       iconKey: 'lilie' },
        { id: 'eustomy',     name: 'Kreator Eustom',        description: 'Subtelne eustomy na wyjątkowe chwile.',                           route: canonicalPath('/kreator/eustomy'),     shopifyHandle: 'eustomy',           staticImage: tulipanCreatorImg,    iconKey: 'eustomy' },
      ],
    },
    placeholderHeight: { mobile: 0, desktop: 798 },
  },
  {
    id: 'google-reviews', type: 'googleReviews', enabled: true, order: 100,
    wrapperClassName: 'hidden md:block',
    config: {
      eyebrow: 'Co mówią klienci',
      title: 'Opinie naszych klientów',
      ctaHref: 'https://www.google.com/maps/search/Bukietowa+Kwiaciarnia+Lublin',
      ctaLabel: 'Wszystkie opinie w Google',
    },
    // Oszacowanie — patrz komentarz na górze pliku (API opinii niedostępne do pomiaru).
    placeholderHeight: { mobile: 0, desktop: 440 },
  },
  {
    id: 'seo-text', type: 'seoTextSection', enabled: true, order: 110,
    config: {
      "eyebrow": "Wybierz swój kwiatowy prezent",
      "title": "Kwiaty na bliskie Ci chwile",
      "tagsLabel": "Najczęściej wybierane",
      "cards": [
            {
                  "iconKey": "bouquet",
                  "title": "Wybierz kwiaty odpowiednie do okazji",
                  "subheading": "Bukiet, flower box czy inna kompozycja?",
                  "body": "Zacznij od osoby, której chcesz podarować kwiaty. Jeśli znasz jej ulubiony kolor, potraktuj go jako punkt wyjścia. Na urodziny możesz wybrać kompozycję pełną różnych barw, a na rocznicę wrócić do kwiatów, które kojarzą się Wam ze wspólną chwilą. W podziękowaniu liczy się dopasowanie gestu do relacji: delikatne pastele i spokojna kolorystyka sprawdzą się także wtedy, gdy nie znasz dobrze gustu odbiorcy.\n\nKlasyczne bukiety to dobry wybór, gdy chcesz wręczyć kwiaty do wazonu. Flower boxy mają formę kompozycji w pudełku, więc sam sposób podania jest częścią prezentu. Kosz lub kwiaty w torebce pozwalają wybrać jeszcze inną oprawę. Porównaj zdjęcia, opis oraz dostępne warianty konkretnego produktu. Zwróć uwagę na kolorystykę, kwiaty i wielkość, a nie tylko nazwę kompozycji. Jeśli prezent ma stanąć na niewielkim biurku, rozważ mniejszą formę; na uroczyste wręczenie możesz wybrać bardziej rozbudowaną.",
                  "linked": [
                        {
                              "word": "Klasyczne bukiety",
                              "href": "/bukiety/"
                        },
                        {
                              "word": "Flower boxy",
                              "href": "/flower-boxy/"
                        }
                  ],
                  "links": []
            },
            {
                  "iconKey": "online",
                  "title": "Zamów kwiaty online w Bukietowej",
                  "subheading": "Gotowa kompozycja czy własny bukiet?",
                  "body": "W sklepie możesz porównać kompozycje bez wychodzenia z domu. Otwórz kartę wybranego produktu i sprawdź, co zawiera dany wariant. Zanim dodasz kwiaty do koszyka, wybierz dostępne opcje i zobacz cenę swojego zestawu. Zdjęcie pomaga ocenić styl, a opis i warianty pozwalają dopasować prezent do planowanego budżetu. Jeżeli zamawiasz dla kogoś, pomyśl też o krótkiej wiadomości, którą chcesz przekazać razem z kwiatami.\n\nGotowa kompozycja ułatwia zakup, kiedy podoba Ci się jej układ i kolorystyka. Gdy masz własny pomysł, opisz go w formularzu zamówienia specjalnego i uzgodnij z kwiaciarnią skład, cenę oraz termin. Nie musisz znać nazw wszystkich odmian, żeby zamówić prezent. Możesz zacząć od koloru, formy lub okazji, a w razie wątpliwości poprosić kwiaciarnię o pomoc przed złożeniem zamówienia.",
                  "linked": [
                        {
                              "word": "formularzu zamówienia specjalnego",
                              "href": "/zamowienie-specjalne/"
                        }
                  ],
                  "links": []
            },
            {
                  "iconKey": "delivery",
                  "title": "Poczta kwiatowa w Lublinie - kwiaty dla bliskich",
                  "body": "Nie zawsze możesz osobiście wręczyć prezent. Poczta kwiatowa w Lublinie pozwala zamówić kwiaty dla osoby, która tu mieszka lub pracuje, także wtedy, gdy Ty jesteś w innym miejscu. W Bukietowej wybierasz kompozycję online i podajesz adres odbiorcy. To sposób na urodzinową niespodziankę, podziękowanie za pomoc albo zwykły gest pamięci bez czekania na szczególną okazję.\n\nPrzed zakupem sprawdź zasady dostawy: obsługiwany adres, dostępny termin i koszt. Dostawa kwiatów w Lublinie i okolicach zależy od miejsca doręczenia oraz wybranej daty. Jeśli zależy Ci na konkretnym dniu, zacznij właśnie od sprawdzenia terminu. Do zamówienia przygotuj pełne dane odbiorcy i informacje ułatwiające dotarcie pod adres. Szczegóły dotyczące przedziałów godzinowych i nieobecności odbiorcy znajdziesz na stronie dostawy, aby móc zaplanować prezent świadomie.",
                  "linked": [
                        {
                              "word": "zasady dostawy",
                              "href": "/dostawa/"
                        }
                  ],
                  "links": []
            },
            {
                  "iconKey": "store",
                  "title": "Odbiór w kwiaciarni i pomoc w wyborze",
                  "body": "Chcesz wręczyć kwiaty osobiście? Przy zamówieniu możesz wybrać odbiór w kwiaciarni. To wygodna opcja przed spotkaniem, rodzinnym obiadem czy wizytą u bliskiej osoby. Sprawdź adres punktu i godziny odbioru, a następnie dopasuj termin do swoich planów. Czas przygotowania zależy od rodzaju kompozycji i godziny odbioru, dlatego przy pilnym zamówieniu warto ustalić szczegóły z kwiaciarnią.\n\nGdy wahasz się między kilkoma propozycjami, opowiedz, dla kogo wybierasz kwiaty, na jaką okazję i jaki budżet chcesz przeznaczyć. Przydatna będzie też informacja o ulubionych barwach odbiorcy lub formie, którą bierzesz pod uwagę. Nie musisz mieć gotowego projektu: kilka konkretnych wskazówek ułatwi rozmowę. Na stronie kontaktu znajdziesz dane naszych kwiaciarni. Możesz dopytać o wybraną kompozycję i jej dostępność, zanim podejmiesz decyzję.",
                  "linked": [
                        {
                              "word": "stronie kontaktu",
                              "href": "/kontakt/"
                        }
                  ],
                  "links": []
            }
      ],
      "tags": []
},
    placeholderHeight: { mobile: 616, desktop: 1200 },
  },
  {
    id: 'footer', type: 'footer', enabled: true, order: 120,
    config: FOOTER_CONFIG,
    placeholderHeight: { mobile: 1040, desktop: 1257 },
  },
];
